import React, { FC, useState } from 'react';
import { useTranslate, usePermissions } from 'react-admin';
import Grid from '@material-ui/core/Grid';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import Accordion from '@material-ui/core/Accordion';
import { withStyles } from '@material-ui/core/styles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Alert } from '@material-ui/lab';
import { AmountField, AccordionSummary, AccordionDetails, H2, TextField } from '~/components';
import { Section } from '~/layout';
import TableCell from '~/components/TableCell';
import BalancesTable from '~/components/ra/Accounts/BalancesTable';
import { HeadTransaction } from '~/types/HeadTransaction';
import { Account } from '~/types/account';
import OperationTypeField from '~/components/OperationTypeField';
import { Longdash } from '~/utils';

const AccordionSummaryStyled = withStyles({
  root: {
    '&.Mui-focused': {
      backgroundColor: 'inherit',
    },
  },
})((props: any) => <AccordionSummary {...props} />);

const AccountsWithAlias: FC<{ accounts: Account[] }> = ({ accounts }) => {
  const classes = useStyles();
  const { permissions } = usePermissions();
  const [expanded, setExpanded] = useState<{ [x: string]: boolean }>({});

  const filtered = accounts.filter(
    (a) => a.balances && a.balances.length > 0 && a.productTemplate?.category !== 'CNL_ACCOUNT'
  );

  if (!permissions || filtered.length === 0) return null;

  return (
    <div>
      {filtered.map((account, index) => {
        const { balances, number, alias } = account;
        const key = account.id as string;
        const isExpanded = Boolean(expanded[key]);
        const heading = alias ? `${number} (${alias})` : number;
        const currentAvailable = balances?.find(
          (item) => item.code === 'CURRENT_AVAILABLE' || item.code === 'CURRENT_FLOAT'
        )?.amount;
        return (
          <Grid key={index} item xs={12}>
            <Accordion
              expanded={isExpanded}
              onChange={() => setExpanded((prev) => ({ ...prev, [key]: !isExpanded }))}
            >
              <AccordionSummaryStyled
                aria-controls={`${key}-content`}
                id={`${key}-header`}
                expanded={isExpanded}
                heading={<TextField showLink={`/accounts/${account.id}/show`}>{heading}</TextField>}
                actionNode={
                  <Grid container justify='flex-end' spacing={1}>
                    <Grid item alignItems='center' className={classes.balanceContainer}>
                      {currentAvailable && <AmountField amount={currentAvailable} />}
                    </Grid>
                  </Grid>
                }
                actionAlwaysVisible
              />
              <AccordionDetails>
                {isExpanded && <BalancesTable balances={account.balances || []} />}
              </AccordionDetails>
            </Accordion>
          </Grid>
        );
      })}
    </div>
  );
};

interface Props {
  record?: HeadTransaction;
  [x: string]: any;
}

const ProcessingTab: FC<Props> = (props) => {
  const { record } = props;
  const translate = useTranslate();
  const t = (key: string) => translate(`resources.headTransactions.show.processing.${key}`);
  const td = (key: string) => translate(`resources.headTransactions.show.details.${key}`);
  const classes = useStyles();

  if (!record) return null;
  const trx = record as HeadTransaction;

  const filteredAccounts = Array.isArray(trx?.accounts)
    ? trx.accounts.filter((a) => a.balances && a.balances.length > 0)
    : null;

  return (
    <Section noPaddings>
      {/* Transfer Plan */}
      <Grid container className={classes.bottomSpacing}>
        <Grid item xs={12}>
          <H2 topSpacing={0} bottomSpacing={2}>
            {t('transferPlan')}
          </H2>
          <Alert severity='info' className={classes.disclaimer}>
            <Typography variant='body2'>{t('disclaimer')}</Typography>
          </Alert>
        </Grid>
        <Grid item xs={12}>
          <Section fullWidth noPaddings>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{td('transfer.amount')}</TableCell>
                  <TableCell>{td('transfer.from')}</TableCell>
                  <TableCell>{td('transfer.to')}</TableCell>
                  <TableCell>{td('transfer.type')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trx.transfers.map((transfer) => (
                  <TableRow key={transfer.id}>
                    <TableCell>
                      <AmountField amount={transfer.amount} />
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const a = trx.accounts.find((a) => a.id === transfer.from);
                        if (!a) return Longdash;
                        return (
                          <TextField
                            labelStyle={{ fontSize: 11, opacity: 0.5 }}
                            label={a?.alias}
                            showLink={`/accounts/${a?.id}/show`}
                          >
                            {a?.number}
                          </TextField>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const a = trx.accounts.find((a) => a.id === transfer.to);
                        if (!a) return Longdash;
                        return (
                          <TextField
                            labelStyle={{ fontSize: 11, opacity: 0.5 }}
                            label={a?.alias}
                            showLink={`/accounts/${a?.id}/show`}
                          >
                            {a?.number}
                          </TextField>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <OperationTypeField operationType={transfer.operationType} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Section>
        </Grid>
      </Grid>

      {/* Balances after transaction posting */}
      {Array.isArray(filteredAccounts) && filteredAccounts.length > 0 && (
        <Grid container className={classes.bottomSpacing}>
          <Grid item xs={12}>
            <H2 topSpacing={2} bottomSpacing={2}>
              {td('balancesAfterPosting')}
            </H2>
          </Grid>
          <Grid item xs={12}>
            <AccountsWithAlias accounts={trx?.accounts || []} />
          </Grid>
        </Grid>
      )}
    </Section>
  );
};

const useStyles = makeStyles((theme) => ({
  bottomSpacing: {
    paddingBottom: theme.spacing(2),
  },
  disclaimer: {
    marginBottom: theme.spacing(2),
  },
  balanceContainer: {
    display: 'flex',
  },
}));

export default ProcessingTab;
